/**
 * /check-ins — every device that has come through the door, newest first.
 *
 * Session-shaped storage, device-shaped question: a session with three
 * machines on it reads as three rows, because what gets asked on the floor is
 * "where's the Dell" and "which job is ABC123 on", not "what was session 0042".
 *
 * Completed check-ins are deliberately absent from the work-item dashboard
 * (see DASHBOARD_LIFECYCLE_STATUSES in sm4rt-api), so before this page there
 * was no way to browse them at all — you had to find the converted job first.
 *
 * Two limits are surfaced rather than hidden, because Dataverse imposes them:
 *  - no `$skip`, so this is one page of the most recent check-ins, not a
 *    paginated archive;
 *  - the serial lives inside the draft job's inspection JSON, which OData
 *    can't filter on, so search runs over the loaded rows only.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type { CheckInSessionListItem } from '@techgitter/contracts';

import {
  SecondaryToolbar,
  ToolbarSearch,
  ToolbarSelect,
  ToolbarSpacer,
  ToolbarCount,
} from '../components/toolbar';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../components/table';
import { Chip, toneFromMuiColor } from '../components/chip';
import {
  intakeTypeLabel,
  lifecycleChipColor,
  lifecycleLabel,
} from '../components/check-in/sessionDetail/sessionDetailHelpers';
import { fetchCheckInSessions } from '../services/checkInApi';
import {
  filterDeviceRows,
  sortDeviceRows,
  toDeviceRows,
  type CheckInDeviceRow,
} from '../utils/checkInDeviceRows';
import { checkInDeviceLabel } from '@techgitter/contracts';

// One page, sized to hold a useful stretch of history. There is no $skip in
// Dataverse, so a larger page is the only lever available.
const PAGE_SIZE = 200;


const STATUS_OPTIONS = [
  { value: 'checked_in', label: 'Checked in' },
  { value: '', label: 'Any status' },
  { value: 'inspection_in_progress', label: 'Being inspected' },
  { value: 'cancelled', label: 'Cancelled' },
];

const INTAKE_OPTIONS = [
  { value: '', label: 'Any intake' },
  { value: 'in_store', label: 'In-Store' },
  { value: 'express', label: 'Express' },
  { value: 'online', label: 'Online' },
  { value: 'in_store_return', label: 'Return' },
  { value: 'in_store_dropoff', label: 'Drop off' },
];

const INSPECTION_TONE = {
  complete: 'success',
  in_progress: 'warning',
  not_started: 'neutral',
} as const;

const INSPECTION_LABEL: Record<string, string> = {
  complete: 'Complete',
  in_progress: 'In progress',
  not_started: 'Not started',
};

/** `17 Sep 2:14 pm` in the shop's own timezone — drop-off times are local facts. */
const formatWhen = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Australia/Sydney',
  });
};

/** Serial, or the reason there isn't one. Phones record an IMEI here. */
const renderSerial = (row: CheckInDeviceRow): React.ReactNode => {
  const { device } = row;
  const prefix = device.deviceType === 'phone' ? 'IMEI' : 'S/N';
  if (device.serialNumber) {
    return (
      <span>
        <span style={{ color: '#8a97a4' }}>{prefix}</span> {device.serialNumber}
      </span>
    );
  }
  if (device.serialNotLegible) return <Chip tone="neutral">Not legible</Chip>;
  // Nothing recorded yet is different from "no serial on the device" — the
  // inspection status column says which.
  return <span style={{ color: '#b3bcc5' }}>—</span>;
};

export const CheckInsPage: React.FC = () => {
  const navigate = useNavigate();

  const [sessions, setSessions] = useState<CheckInSessionListItem[]>([]);
  const [totalSessions, setTotalSessions] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('checked_in');
  const [intakeType, setIntakeType] = useState('');

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    fetchCheckInSessions({
      pageSize: PAGE_SIZE,
      lifecycleStatus: status ? (status as CheckInSessionListItem['lifecycleStatus']) : undefined,
      intakeType: intakeType ? (intakeType as CheckInSessionListItem['intakeType']) : undefined,
      // Completed check-ins sort by when they completed; expiry (the default)
      // is meaningless once a session is closed.
      orderBy: status === 'checked_in' ? 'completedAtDesc' : 'createdAtDesc',
      signal: ctrl.signal,
    })
      .then(res => {
        setSessions(res.items);
        setTotalSessions(res.pagination?.totalItems ?? res.items.length);
      })
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message);
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [status, intakeType, retryNonce]);

  const rows = useMemo(() => sortDeviceRows(toDeviceRows(sessions)), [sessions]);
  const visible = useMemo(() => filterDeviceRows(rows, search), [rows, search]);

  const truncated = totalSessions > sessions.length;

  return (
    <>
      <SecondaryToolbar floating>
        <ToolbarSearch
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search serial, make, customer, ticket…"
          maxWidth={480}
        />
        <ToolbarSelect label="Status" value={status} onChange={setStatus} options={STATUS_OPTIONS} />
        <ToolbarSelect
          label="Intake"
          value={intakeType}
          onChange={setIntakeType}
          options={INTAKE_OPTIONS}
        />
        <ToolbarSpacer />
        <ToolbarCount>
          {visible.length === 0
            ? 'No devices'
            : search
              ? `${visible.length} of ${rows.length} devices`
              : `${rows.length} devices`}
        </ToolbarCount>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px', maxWidth: 1600, mx: 'auto' }}>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => setRetryNonce(n => n + 1)}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {truncated && !loading && (
          // Said plainly rather than left for someone to discover when a
          // search comes up empty for a device they know is here.
          <Alert severity="info" sx={{ mb: 2 }}>
            Showing the {sessions.length} most recent check-ins of {totalSessions}. Search covers
            these only — narrow by status or intake to reach older ones.
          </Alert>
        )}

        <DataTableCard>
          <DataTable>
            <thead>
              <tr>
                <DataTableHeadCell>Checked in</DataTableHeadCell>
                <DataTableHeadCell>Customer</DataTableHeadCell>
                <DataTableHeadCell>Device</DataTableHeadCell>
                <DataTableHeadCell>Make</DataTableHeadCell>
                <DataTableHeadCell>Serial</DataTableHeadCell>
                <DataTableHeadCell>Service</DataTableHeadCell>
                <DataTableHeadCell>Ticket</DataTableHeadCell>
                <DataTableHeadCell>Inspection</DataTableHeadCell>
                <DataTableHeadCell>Intake</DataTableHeadCell>
                <DataTableHeadCell>Status</DataTableHeadCell>
              </tr>
            </thead>
            <tbody>
              {loading &&
                Array.from({ length: 8 }).map((_, i) => (
                  <DataTableRow key={`skeleton-${i}`}>
                    {Array.from({ length: 10 }).map((__, j) => (
                      <DataTableCell key={j}>
                        <Skeleton variant="text" />
                      </DataTableCell>
                    ))}
                  </DataTableRow>
                ))}
              {!loading && visible.length === 0 && !error && (
                <DataTableEmpty colSpan={10}>
                  {search ? 'No devices match that search' : 'No check-ins found'}
                </DataTableEmpty>
              )}
              {!loading &&
                visible.map(row => (
                  <DataTableRow
                    key={row.key}
                    // Straight to the device, which is where the inspection
                    // and the serial correction live.
                    onClick={() =>
                      navigate(`/check-in/session/${row.sessionId}/job/${row.device.id}`)
                    }
                  >
                    <DataTableCell bold>{formatWhen(row.occurredAt)}</DataTableCell>
                    <DataTableCell>{row.customerLabel}</DataTableCell>
                    <DataTableCell>
                      {checkInDeviceLabel(row.device)}
                    </DataTableCell>
                    <DataTableCell>{row.device.manufacturer || '—'}</DataTableCell>
                    <DataTableCell>{renderSerial(row)}</DataTableCell>
                    <DataTableCell>{row.device.serviceSummary || '—'}</DataTableCell>
                    <DataTableCell>
                      {row.device.repairShoprTicketNumber || row.device.repairShoprTicketId ? (
                        <Chip tone="info">
                          {`#${row.device.repairShoprTicketNumber ?? row.device.repairShoprTicketId}`}
                        </Chip>
                      ) : (
                        '—'
                      )}
                    </DataTableCell>
                    <DataTableCell>
                      <Chip tone={INSPECTION_TONE[row.device.inspectionStatus as keyof typeof INSPECTION_TONE] ?? 'neutral'}>
                        {INSPECTION_LABEL[row.device.inspectionStatus] ?? row.device.inspectionStatus}
                      </Chip>
                    </DataTableCell>
                    <DataTableCell>{intakeTypeLabel(row.intakeType)}</DataTableCell>
                    <DataTableCell>
                      <Chip tone={toneFromMuiColor(lifecycleChipColor(row.lifecycleStatus))}>
                        {lifecycleLabel(row.lifecycleStatus)}
                      </Chip>
                    </DataTableCell>
                  </DataTableRow>
                ))}
            </tbody>
          </DataTable>
        </DataTableCard>
      </Box>
    </>
  );
};

export default CheckInsPage;
